import * as fromMigrations from './migrations.actions';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  IMigration,
  IAccountMigration,
  MigrationState,
  IMigrationStatus,
} from '@shared';
import * as moment from 'moment';

export const MIGRATIONS_FEATURE_KEY = 'migrations';

export interface State {
  allMigrations: IMigration[];
  accountMigrations: IAccountMigration[];
  migrationsLoading: boolean;
  timeLineInterval: number;
  searchValue: string;
  stateFilter: string[];
  accountMigraionProgress: IMigrationStatus;
  progressLoading: boolean;
  accountPASMigrationProgress: IMigration;
  pasProgressLoading: boolean;
}

export const initialState: State = {
  allMigrations: [],
  accountMigrations: [],
  migrationsLoading: false,
  timeLineInterval: 30,
  searchValue: '',
  stateFilter: [],
  accountMigraionProgress: null,
  progressLoading: false,
  accountPASMigrationProgress: null,
  pasProgressLoading: false,
};

export function reducer(
  state = initialState,
  action: fromMigrations.MigrationsActions,
): State {
  switch (action.type) {
    case fromMigrations.MigrationsActionTypes.GetMigrations: {
      return {
        ...state,
        migrationsLoading: true,
      };
    }

    case fromMigrations.MigrationsActionTypes.GetMigrationsSuccess: {
      const migrations = [];
      [...action.payload].forEach((m) => {
        m.status === MigrationState.Running
          ? migrations.unshift(m)
          : migrations.push(m);
      });
      return {
        ...state,
        allMigrations: migrations,
        migrationsLoading: false,
      };
    }

    case fromMigrations.MigrationsActionTypes.GetMigrationsFail: {
      return {
        ...state,
        allMigrations: [],
        migrationsLoading: false,
      };
    }

    case fromMigrations.MigrationsActionTypes.GetAccountMigrations: {
      return {
        ...state,
        migrationsLoading: true,
        accountMigraionProgress: null,
      };
    }

    case fromMigrations.MigrationsActionTypes.GetAccountMigrationsSuccess: {
      return {
        ...state,
        accountMigrations: action.payload,
        migrationsLoading: false,
      };
    }

    case fromMigrations.MigrationsActionTypes.GetAccountMigrationProgress: {
      return {
        ...state,
        progressLoading: true,
      };
    }

    case fromMigrations.MigrationsActionTypes.GetAccountMigrationProgressFail: {
      return {
        ...state,
        progressLoading: false,
      };
    }

    case fromMigrations.MigrationsActionTypes
      .GetAccountMigrationProgressSuccess: {
      const { progress } = action.payload;

      return {
        ...state,
        accountMigraionProgress: progress,
        progressLoading: false,
      };
    }

    case fromMigrations.MigrationsActionTypes.GetAccountMigrationsFail: {
      return {
        ...state,
        accountMigrations: [],
        migrationsLoading: false,
      };
    }

    case fromMigrations.MigrationsActionTypes.UpdateSearchValues: {
      const { query, dateInterval, stateFilter } = action.payload;
      return {
        ...state,
        searchValue: query,
        timeLineInterval: dateInterval,
        stateFilter: stateFilter,
      };
    }

    case fromMigrations.MigrationsActionTypes.GetAccountPASMigrationsSuccess: {
      const { migration } = action.payload;
      return {
        ...state,
        pasProgressLoading: false,
        accountPASMigrationProgress: migration,
      };
    }

    case fromMigrations.MigrationsActionTypes.GetAccountPASMigrations: {
      return {
        ...state,
        pasProgressLoading: true,
        accountPASMigrationProgress: null,
      };
    }

    case fromMigrations.MigrationsActionTypes.GetAccountPASMigrationsFail: {
      return {
        ...state,
        pasProgressLoading: false,
      };
    }

    default:
      return state;
  }
}

const getMigrationsState = createFeatureSelector<State>(MIGRATIONS_FEATURE_KEY);

export const getAllMigrations = createSelector(
  getMigrationsState,
  ({ ...state }) => state.allMigrations,
);

export const getTimelineInterval = createSelector(
  getMigrationsState,
  ({ ...state }) => state.timeLineInterval,
);

export const getSearchValue = createSelector(
  getMigrationsState,
  ({ ...state }) => state.searchValue,
);

export const getStateFilter = createSelector(
  getMigrationsState,
  ({ ...state }) => state.stateFilter,
);

export const getSuccessfulMigrations = createSelector(
  getMigrationsState,
  ({ ...state }) => {
    return state.allMigrations.filter(
      (migration) => migration.status === MigrationState.Success,
    );
  },
);

export const getFailedMigrations = createSelector(
  getMigrationsState,
  ({ ...state }) => {
    return state.allMigrations.filter(
      (migration) => migration.status === MigrationState.Failed,
    );
  },
);

export const getAllSuccessfulMigrations = createSelector(
  getSuccessfulMigrations,
  getTimelineInterval,
  getSearchValue,
  getStateFilter,
  (migrations, timeLineInterval, searchValue, stateFilter) => {
    const dates = [...new Array(timeLineInterval)]
      .map((i, idx) =>
        moment().startOf('day').subtract(idx, 'days').format('YYYY-MM-DD'),
      )
      .reverse();
    const data = { labels: dates, data: [] };
    if (!stateFilter.length || stateFilter.includes(MigrationState.Success)) {
      const successfulMigrations = migrations.filter(
        (migration) =>
          searchValue === '' ||
          migration.oneAccountId
            .toLowerCase()
            .indexOf(searchValue.toLowerCase()) > -1,
      );
      dates.forEach((date) => {
        const dataArray = successfulMigrations.filter((migration) => {
          let lastSyncInUnix = null;
          if (migration && migration.order && migration.order.results) {
            const migrationResults = migration.order.results;
            if (
              migrationResults.optOutAll &&
              migrationResults.optOutAll.lastSyncTime
            ) {
              lastSyncInUnix = moment(
                migrationResults.optOutAll.lastSyncTime,
              ).valueOf();
            }
            if (
              migrationResults.subscribers &&
              migrationResults.subscribers.lastSyncTime
            ) {
              lastSyncInUnix =
                lastSyncInUnix >
                moment(migrationResults.subscribers.lastSyncTime).valueOf()
                  ? lastSyncInUnix
                  : moment(migrationResults.subscribers.lastSyncTime).valueOf();
            }
          }
          return moment(lastSyncInUnix).format('YYYY-MM-DD') === date;
        });
        data.data.push(dataArray.length);
      });
    }
    return data;
  },
);

export const getAllFailedMigrations = createSelector(
  getFailedMigrations,
  getTimelineInterval,
  getSearchValue,
  getStateFilter,
  (migrations, timeLineInterval, searchValue, stateFilter) => {
    const dates = [...new Array(timeLineInterval)]
      .map((i, idx) =>
        moment().startOf('day').subtract(idx, 'days').format('YYYY-MM-DD'),
      )
      .reverse();
    const data = { labels: dates, data: [] };
    if (!stateFilter.length || stateFilter.includes(MigrationState.Failed)) {
      const failedMigrations = migrations.filter(
        (migration) =>
          searchValue === '' ||
          migration.oneAccountId
            .toLowerCase()
            .indexOf(searchValue.toLowerCase()) > -1,
      );
      dates.forEach((date) => {
        const dataArray = failedMigrations.filter((migration) => {
          let lastSyncInUnix = null;
          if (migration && migration.order && migration.order.results) {
            const migrationResults = migration.order.results;
            if (
              migrationResults.optOutAll &&
              migrationResults.optOutAll.lastSyncTime
            ) {
              lastSyncInUnix = moment(
                migrationResults.optOutAll.lastSyncTime,
              ).valueOf();
            }
            if (
              migrationResults.subscribers &&
              migrationResults.subscribers.lastSyncTime
            ) {
              lastSyncInUnix =
                lastSyncInUnix >
                moment(migrationResults.subscribers.lastSyncTime).valueOf()
                  ? lastSyncInUnix
                  : moment(migrationResults.subscribers.lastSyncTime).valueOf();
            }
          }
          return moment(lastSyncInUnix).format('YYYY-MM-DD') === date;
        });
        data.data.push(dataArray.length);
      });
    }
    return data;
  },
);

export const getAccountMigrations = createSelector(
  getMigrationsState,
  ({ ...state }) => state.accountMigrations,
);

export const getMigrationsLoading = createSelector(
  getMigrationsState,
  ({ ...state }) => state.migrationsLoading,
);

export const getAccountMigrationProgress = createSelector(
  getMigrationsState,
  ({ ...state }) => state.accountMigraionProgress,
);

export const getProgressIsLoading = createSelector(
  getMigrationsState,
  ({ ...state }) => state.progressLoading,
);

export const getAccountPASMigrationProgress = createSelector(
  getMigrationsState,
  ({ ...state }) => state.accountPASMigrationProgress,
);

export const getPASProgressIsLoading = createSelector(
  getMigrationsState,
  ({ ...state }) => state.pasProgressLoading,
);
